/**
 * Expression AST.
 *
 * The allowlist in `FUNCTIONS` is the security boundary required by PRD §6.5:
 * a name that is not in this table is a parse error, so an imported project
 * file cannot reach anything outside it. Nothing here is ever handed to `eval`
 * or `new Function` — see compile.ts.
 */

export type Node =
  | { kind: 'num'; value: number }
  | { kind: 'ref'; name: string }
  | { kind: 'neg'; operand: Node }
  | { kind: 'binary'; op: BinaryOp; left: Node; right: Node }
  | { kind: 'call'; name: FunctionName; args: Node[] }
  | { kind: 'if'; cond: Node; then: Node; otherwise: Node }
  | { kind: 'compare'; op: CompareOp; left: Node; right: Node };

export type BinaryOp = '+' | '-' | '*' | '/' | '^';
export type CompareOp = '<' | '<=' | '>' | '>=' | '==' | '!=';

/** Arity table doubling as the function allowlist. */
export const FUNCTIONS = {
  sqrt: 1,
  exp: 1,
  ln: 1,
  log10: 1,
  abs: 1,
  sin: 1,
  cos: 1,
  tan: 1,
  asin: 1,
  acos: 1,
  atan: 1,
  atan2: 2,
  floor: 1,
  ceil: 1,
  round: 1,
  min: 2,
  max: 2,
  clamp: 3,
} as const;

export type FunctionName = keyof typeof FUNCTIONS;

/** Constants usable by name in any expression. */
export const CONSTANTS: Record<string, number> = {
  pi: Math.PI,
  e: Math.E,
};

export function isFunctionName(name: string): name is FunctionName {
  return Object.prototype.hasOwnProperty.call(FUNCTIONS, name);
}

/** Collect every free identifier referenced by an expression, in first-seen order. */
export function referencedNames(node: Node, into: Set<string> = new Set()): Set<string> {
  switch (node.kind) {
    case 'num':
      break;
    case 'ref':
      if (!(node.name in CONSTANTS)) into.add(node.name);
      break;
    case 'neg':
      referencedNames(node.operand, into);
      break;
    case 'binary':
    case 'compare':
      referencedNames(node.left, into);
      referencedNames(node.right, into);
      break;
    case 'call':
      for (const a of node.args) referencedNames(a, into);
      break;
    case 'if':
      referencedNames(node.cond, into);
      referencedNames(node.then, into);
      referencedNames(node.otherwise, into);
      break;
  }
  return into;
}

/**
 * True when the expression is smooth enough for symbolic differentiation to be
 * trustworthy everywhere.
 *
 * `abs`, `min`, `max`, `floor`, `ceil`, `round` and `if` all introduce kinks or
 * jumps. Their derivatives exist almost everywhere but are meaningless at the
 * switching point, so RSS and WCA fall back to finite differences and flag the
 * result rather than pretending otherwise (PRD §4.5).
 */
export function isSmooth(node: Node): boolean {
  switch (node.kind) {
    case 'num':
    case 'ref':
      return true;
    case 'neg':
      return isSmooth(node.operand);
    case 'binary':
      return isSmooth(node.left) && isSmooth(node.right);
    case 'compare':
      return false;
    case 'if':
      return false;
    case 'call':
      if (
        node.name === 'abs' ||
        node.name === 'min' ||
        node.name === 'max' ||
        node.name === 'floor' ||
        node.name === 'ceil' ||
        node.name === 'round'
      ) {
        return false;
      }
      return node.args.every(isSmooth);
  }
}

/** Render an AST back to source, mainly for reports and debugging. */
export function formatExpr(node: Node): string {
  switch (node.kind) {
    case 'num':
      return String(node.value);
    case 'ref':
      return node.name;
    case 'neg':
      return `-(${formatExpr(node.operand)})`;
    case 'binary':
      return `(${formatExpr(node.left)} ${node.op} ${formatExpr(node.right)})`;
    case 'compare':
      return `(${formatExpr(node.left)} ${node.op} ${formatExpr(node.right)})`;
    case 'call':
      return `${node.name}(${node.args.map(formatExpr).join(', ')})`;
    case 'if':
      return `if(${formatExpr(node.cond)}, ${formatExpr(node.then)}, ${formatExpr(node.otherwise)})`;
  }
}

// --- Constructors with light algebraic simplification ------------------------
// Symbolic differentiation generates a great deal of `x*1`, `x+0` and `0*y`.
// Folding them at construction keeps derivative trees small enough to read in
// the UI and cheap enough to evaluate in the WCA corner search.

export const num = (value: number): Node => ({ kind: 'num', value });

const isZero = (n: Node) => n.kind === 'num' && n.value === 0;
const isOne = (n: Node) => n.kind === 'num' && n.value === 1;

export function add(left: Node, right: Node): Node {
  if (isZero(left)) return right;
  if (isZero(right)) return left;
  if (left.kind === 'num' && right.kind === 'num') return num(left.value + right.value);
  return { kind: 'binary', op: '+', left, right };
}

export function sub(left: Node, right: Node): Node {
  if (isZero(right)) return left;
  if (left.kind === 'num' && right.kind === 'num') return num(left.value - right.value);
  if (isZero(left)) return neg(right);
  return { kind: 'binary', op: '-', left, right };
}

export function mul(left: Node, right: Node): Node {
  if (isZero(left) || isZero(right)) return num(0);
  if (isOne(left)) return right;
  if (isOne(right)) return left;
  if (left.kind === 'num' && right.kind === 'num') return num(left.value * right.value);
  return { kind: 'binary', op: '*', left, right };
}

export function div(left: Node, right: Node): Node {
  if (isZero(left)) return num(0);
  if (isOne(right)) return left;
  if (left.kind === 'num' && right.kind === 'num' && right.value !== 0) {
    return num(left.value / right.value);
  }
  return { kind: 'binary', op: '/', left, right };
}

export function pow(left: Node, right: Node): Node {
  if (isOne(right)) return left;
  if (isZero(right)) return num(1);
  return { kind: 'binary', op: '^', left, right };
}

export function neg(operand: Node): Node {
  if (operand.kind === 'num') return num(-operand.value);
  if (operand.kind === 'neg') return operand.operand;
  return { kind: 'neg', operand };
}

export function call(name: FunctionName, args: Node[]): Node {
  return { kind: 'call', name, args };
}
